/**
 * Molecular Service
 * Frontend wrapper for /api/v1/molecular/* (AlphaFold via backend proxy).
 */
import axios from 'axios';

// API base resolved via config/api.js so prod (REACT_APP_API_BASE_URL='') uses relative URLs through Nginx; localhost fallback only in dev.
import { API_BASE_URL as ROOT_API } from '../config/api';
const API_BASE_URL = `${ROOT_API}/api/v1`;

const molecularService = {
  /**
   * Fetch summary metadata for a UniProt accession's AlphaFold structure.
   * Returns { uniprot_accession, uniprot_description, gene, organism_scientific_name,
   *           sequence_length, pdb_url, global_metric_value, ... }
   */
  async getStructureMetadata(uniprotId) {
    const response = await axios.get(`${API_BASE_URL}/molecular/structure/${uniprotId}`);
    return response.data;
  },

  /**
   * Return the URL that serves the PDB text via our backend proxy.
   * The viewer hands this URL to 3Dmol so the browser pulls it from
   * our domain (avoiding cross-origin issues with the EBI CDN).
   */
  getPdbProxyUrl(uniprotId) {
    return `${API_BASE_URL}/molecular/structure/${uniprotId}/pdb`;
  },

  /**
   * Fetch the curated UniProt targets for a medicinal plant (based on its category).
   * Returns { plant_id, category, active_compounds, targets: [{ uniprot_id, name, rationale }] }
   */
  async getPlantTargets(plantId) {
    const response = await axios.get(`${API_BASE_URL}/molecular/plant/${plantId}/targets`);
    return response.data;
  },

  /**
   * Fetch the full phytochemistry graph for a medicinal plant.
   * Returns { plant_id, common_name, category, source, nodes: [...], edges: [...] }
   * where nodes have type "plant" | "compound" | "protein" and edges connect them
   * with "contains" (plant→compound) or "inhibits"/"activates"/"modulates" (compound→protein).
   */
  async getPhytochemistryMap(plantId) {
    const response = await axios.get(`${API_BASE_URL}/molecular/plant/${plantId}/phytochemistry-map`);
    return response.data;
  },
};

export default molecularService;
